import React, { useCallback, useEffect, useRef } from 'react';
import SlickSlider from 'react-slick';
import { styled } from 'linaria/react';
import { Above } from '@jetshop/ui/Breakpoints';
import { useIntl } from '@jetshop/intl';
import { theme } from '../Theme';
import t from '@jetshop/intl';
import { ReactComponent as Carrot } from '../../svg/Carrot.svg';

import 'slick-carousel/slick/slick.css';

import { ElevateProductCard } from './ElevateProductCard';
import { useElevate } from './ElevateContext';
import { ElevateSkeletonProductCard } from './ElevateSkeletonProductCard';

const ElevateCardGrid = styled('ul')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto;
  max-width: 90rem;
  column-gap: 10px;
  row-gap: 30px;
  margin: 0 auto;

  ${theme.below.lg} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const RecommendationCarouselWrapper = styled('div')`
  width: 100%;
  position: relative;
  max-width: 90rem;
  margin: auto;
  h2 {
    color: #000;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 100%;
    margin-bottom: 20px;
  }

  .slick-list,
  .slick-slider,
  .slick-track {
    position: relative;
    display: block;
  }

  .slick-loading .slick-slide,
  .slick-loading .slick-track {
    visibility: hidden;
  }

  .slick-slider {
    list-style: none;
    box-sizing: border-box;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    -khtml-user-select: none;
    -ms-touch-action: pan-y;
    touch-action: pan-y;
    -webkit-tap-highlight-color: transparent;
  }

  .slick-list {
    overflow: hidden;
    /* Offset slide padding so edges align with page */
    margin: 0 -5px;
    padding: 0;
  }

  .slick-list:focus {
    outline: 0;
  }

  .slick-slide {
    display: none;
    float: left;
    height: 100%;
    min-height: 1px;
    /* Spacing between cards while edges remain flush */
    padding: 0 5px;

    /* Ensure focusable elements in hidden slides are not focusable */
    &[aria-hidden='true'] {
      * {
        tabindex: -1;
        pointer-events: none;
      }
    }
  }

  [dir='rtl'] .slick-slide {
    float: right;
  }

  .slick-initialized .slick-slide {
    display: block;
  }

  .slick-arrow {
    position: absolute;
    height: 44px;
    width: 44px;
    top: 32%;
    transform: translateY(-50%);
    border-radius: 100%;
    background: #fff;
    display: flex !important;
    text-align: center;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 1;
    cursor: pointer;
    padding: 0;
    box-shadow: 0px 0px 10px -5px rgba(0, 0, 0, 0.2);
    transition: 0.2s;
    border: 0;

    svg {
      height: 19px;
      width: 19px;
    }

    ${theme.below.lg} {
      height: 36px;
      width: 36px;
      svg {
        height: 16px;
        width: 16px;
      }
    }

    use {
      fill: ${theme.colors.primary};
    }

    &:hover {
      transform: translateY(-50%) scale(1.05);
      box-shadow: 0px 0px 10px -3px rgba(0, 0, 0, 0.3);
    }

    &.slick-prev {
      left: 0px;

      svg {
        transform: rotate(90deg);
      }
    }

    &.slick-next {
      right: 0px;

      svg {
        transform: rotate(-90deg);
      }
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }
`;

export const ElevateProductRecommendation = ({
  noTitle,
  title,
  recommendationObject,
  slidesToShowDesktop,
  slidesToShowMobile,
  showVariants = true,
  clickCallback = () => {}
}) => {
  const lengthRef = useRef(0);
  const visibleStartRef = useRef(0);
  const visibleCountRef = useRef(0);
  const { notification, NOTIFICATION_TYPES } = useElevate();
  // Map of slick card index -> whether it is intersecting the viewport
  const isIntersectingByIndexRef = useRef(new Map());
  // Map of slick card index -> productWithIndex (used when it becomes visible)
  const productByIndexRef = useRef(new Map());
  // Ensure we only trigger once per card index
  const hasTrackedByIndexRef = useRef(new Set());

  const translate = useIntl();

  const groupsArray = Array.isArray(recommendationObject.productGroups)
    ? recommendationObject.productGroups
    : recommendationObject.productGroups?.products
    ? [recommendationObject.productGroups]
    : [];

  const maxDesktopSlides = Number.isNaN(parseFloat(slidesToShowDesktop))
    ? 4
    : parseFloat(slidesToShowDesktop);
  const maxMobileSlides = Number.isNaN(parseFloat(slidesToShowMobile))
    ? 2
    : parseFloat(slidesToShowMobile);

  useEffect(() => {
    lengthRef.current = groupsArray.length;
    visibleStartRef.current = 0;
    hasTrackedByIndexRef.current = new Set();
    isIntersectingByIndexRef.current = new Map();
    productByIndexRef.current = new Map();
  }, [groupsArray.length]);

  const nextText = translate('Next product');
  const prevText = translate('Previous product');

  const desktopSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow:
      groupsArray?.length > maxDesktopSlides
        ? maxDesktopSlides
        : groupsArray?.length,
    slidesToScroll: 1,

    nextArrow: (
      <button aria-label={nextText} type="button">
        <Carrot />
      </button>
    ),
    prevArrow: (
      <button aria-label={prevText} type="button">
        <Carrot />
      </button>
    )
  };

  const mobileSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow:
      groupsArray?.length > maxMobileSlides
        ? maxMobileSlides
        : groupsArray?.length,
    slidesToScroll: 1,
    nextArrow: (
      <button aria-label={nextText} type="button">
        <Carrot />
      </button>
    ),
    prevArrow: (
      <button aria-label={prevText} type="button">
        <Carrot />
      </button>
    )
  };

  const normalizeIndex = useCallback(index => {
    const len = lengthRef.current || 1;
    return ((index % len) + len) % len;
  }, []);

  const isIndexInVisibleRange = useCallback(
    indexNormalized => {
      const len = lengthRef.current;
      const count = Math.min(visibleCountRef.current || 0, len);

      if (!len || !count) return false;
      if (count >= len) return true;

      const startNormalized = normalizeIndex(visibleStartRef.current);
      const endInclusive = startNormalized + count - 1;

      // No wrap case
      if (endInclusive < len) {
        return (
          indexNormalized >= startNormalized && indexNormalized <= endInclusive
        );
      }

      // Wrap case
      const endWrapped = endInclusive % len;
      return (
        indexNormalized >= startNormalized || indexNormalized <= endWrapped
      );
    },
    [normalizeIndex]
  );

  const tryTrackVisibleCards = useCallback(() => {
    const len = lengthRef.current;
    const count = Math.min(visibleCountRef.current || 0, len);

    if (!len || !count) return;

    // Track cards in the current slick viewport
    const startNormalized = normalizeIndex(visibleStartRef.current);
    for (let offset = 0; offset < count; offset++) {
      const idx = normalizeIndex(startNormalized + offset);
      if (hasTrackedByIndexRef.current.has(idx)) continue;

      if (!isIntersectingByIndexRef.current.get(idx)) continue;

      const productWithIndex = productByIndexRef.current.get(idx);
      if (!productWithIndex) continue;

      hasTrackedByIndexRef.current.add(idx);
      // This is the "real" inViewCallback behavior we want:
      // only fire once the card is visible in the slick viewport.
    }
  }, [normalizeIndex]);

  // ElevateProductCard calls this during mount (register phase) with (element, productWithIndex).
  // We set up an IntersectionObserver, but we only "fire" the callback when the card is inside the
  // slick visible range.
  const inViewCallback = useCallback(
    (ref, productWithIndex) => {
      if (!ref || !productWithIndex) return undefined;

      const rawIndex = productWithIndex?._gridIndex;
      if (typeof rawIndex !== 'number') return undefined;

      const indexNormalized = normalizeIndex(rawIndex);

      const observer = new IntersectionObserver(
        entries => {
          const entry = entries[0];
          const isIntersecting = Boolean(entry?.isIntersecting);

          isIntersectingByIndexRef.current.set(indexNormalized, isIntersecting);
          productByIndexRef.current.set(indexNormalized, productWithIndex);

          // We might already be intersecting (slick infinite clones).
          // So we must re-check whenever intersection state updates.
          if (isIntersecting && isIndexInVisibleRange(indexNormalized)) {
            tryTrackVisibleCards();
          } else {
            // Still re-check because visible range may have just changed.
            tryTrackVisibleCards();
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(ref);

      return () => {
        observer.disconnect();
      };
    },
    [isIndexInVisibleRange, normalizeIndex, tryTrackVisibleCards]
  );

  const handleClickCallback = (product, index) => {
    notification(NOTIFICATION_TYPES.click, product?.ticket);
    clickCallback(product, index);
  };

  if (!groupsArray?.length || !recommendationObject?.visible) return null;

  return (
    <Above breakpoint="lg">
      {matches => {
        const slideSettings = matches ? desktopSettings : mobileSettings;
        visibleCountRef.current = slideSettings.slidesToShow;
        const nrOfSlides = matches ? maxDesktopSlides : maxMobileSlides;

        return (
          <RecommendationCarouselWrapper
            role="region"
            aria-label="Elevate recommendations"
          >
            {!noTitle && (
              <h2 className="elevate-recommendations-title">
                {title ?? t(recommendationObject?.id)}
              </h2>
            )}

            {recommendationObject?.visualization === 'CAROUSEL' &&
            nrOfSlides <= groupsArray?.length ? (
              <SlickSlider
                {...slideSettings}
                beforeChange={(_, next) => {
                  visibleStartRef.current = next;
                  tryTrackVisibleCards();
                }}
                afterChange={next => {
                  visibleStartRef.current = next;
                  tryTrackVisibleCards();
                }}
              >
                {groupsArray.map((group, index) => {
                  const key =
                    group?.products?.[0]?.key ??
                    group?.key ??
                    `${index}-${group?.products?.length ?? 0}`;

                  return (
                    <ElevateProductCard
                      key={key}
                      as="div"
                      index={index}
                      productGroups={group}
                      clickCallback={handleClickCallback}
                      showVariants={showVariants}
                      inViewCallback={inViewCallback}
                    />
                  );
                })}
              </SlickSlider>
            ) : (
              <ElevateCardGrid>
                {groupsArray.map((group, index) => {
                  return (
                    <ElevateProductCard key={index} productGroups={group} />
                  );
                })}
              </ElevateCardGrid>
            )}
          </RecommendationCarouselWrapper>
        );
      }}
    </Above>
  );
};
